import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as Styles from './ChatHistory.styles';
import IconImage from '@/media/MaiaIA/iconImage.png';
import { SRC } from '../../AiChat.enum';
import {
  IconThumb,
  Modal,
  IconZoom,
  IconOpenEye,
  IconDownload,
  IconDocumentFile,
  StepsAccordion,
  CopySnippet,
  Button,
  IconArrow,
  Tooltip,
} from 'gatorcomponents';
import { IconButton } from '@/pages/common/v1/IconButton';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ATIVACAO_AICHAT_RATING_THUMBS } from '@/config/GrowthBook/constants';
import { useDispatch, useSelector } from 'react-redux';
import { commonActions, merlinActions } from '@/redux/modules';
import useLocale from '@/hooks/useLocale';
import { useHistory } from 'react-router';

const ChatHistory = ({
  history,
  locales,
  loadingMessage,
  loading,
  handleCloseClick,
  handleOpenSupport,
  handleRedirectToMigration,
  renderInHouseChat,
  handleRedirect,
  handleRateMessage,
  ratedMessages,
  fullScreen,
  handleSubmitMerlin,
  openDocument,
  viewFullImage,
  name,
  isFullScreen,
  onToggleFullScreen,
  openSitePreview,
  handleOpenAlerts,
}) => {
  const navHistory = useHistory();
  const [modalContent, setModalContent] = useState(null);
  const historyListRef = useRef(null);
  const withTumbs = useFeatureIsOn(ATIVACAO_AICHAT_RATING_THUMBS);
  const { routes: routesLocale } = useLocale();

  const { pdfUrl } = useSelector(state => state.common);
  const { executeN8nLoading } = useSelector(state => state.merlin);
  const dispatch = useDispatch();

  const closeModal = () => {
    setModalContent(null);
  };

  useEffect(() => {
    if (historyListRef.current || loading) {
      historyListRef.current.scrollTop = historyListRef.current.scrollHeight;
    }
  }, [history.length, loading]);


  const mountRatings = useCallback(({ messageId, message }) => {
    const isActive = ratedMessages && ratedMessages.find(item => item.messageId === messageId && item.rating);
    const isInActive = ratedMessages && ratedMessages.find(item => item.messageId === messageId && !item.rating);

    return (
      <Styles.RatingRow>
        {((message.text || message.html) && message.src === SRC.BOT) && (
          <Tooltip content={locales.tooltips.copyText} preferredPosition="bottomLeft">
            <Styles.SnippetWrapper>
              <CopySnippet
                content={message.text || JSON.stringify(message.html)}
                iconSize="sm"
              />
            </Styles.SnippetWrapper>
          </Tooltip>
        )}

        {(message.id && withTumbs) && (
          <>
            <Tooltip content={locales.tooltips.likeAnswer} preferredPosition="bottomLeft">
              <Styles.RoundedButton
                onClick={() => handleRateMessage({ messageId, rating: true })}
              >
                <IconThumb
                  size="sm"
                  color={isActive ? 'primaryMedium' : 'lowMedium'}
                />
              </Styles.RoundedButton>
            </Tooltip>

            <Tooltip content={locales.tooltips.dislikeAnswer} preferredPosition="bottomLeft">
              <Styles.RoundedButton
                onClick={() => handleRateMessage({ messageId, rating: false })}
              >
                <IconThumb
                  size="sm"
                  color={isInActive ? 'primaryMedium' : 'lowMedium'}
                  direction="right"
                />
              </Styles.RoundedButton>
            </Tooltip>
          </>
        )}
      </Styles.RatingRow>
    );
  }, [
    handleRateMessage,
    ratedMessages,
    withTumbs,
    locales.tooltips.copyText,
    locales.tooltips.likeAnswer,
    locales.tooltips.dislikeAnswer,
  ]);

  const maiaIconRow = () => (
    <Styles.NameRow>
      <Styles.MaiaIcon src={IconImage} />
      <Styles.AiName>{name}</Styles.AiName>
    </Styles.NameRow>
  );

  const getPdf = ({ pdf, type, action }) => {
    const urlType = type === 'download' ? 'download' : '';
    dispatch(commonActions.pdf.request({ pdfUrl: pdf, urlType, fallBackAction: action }));
  };


  const mountText = ({ message }) => (
    <Styles.HistoryRow data-testid="textMessage">
      {message.src === SRC.BOT && (
        maiaIconRow()
      )}

      {message.html && (
        <Styles.ResponseText $src={message.src} $isFullScreen={fullScreen} dangerouslySetInnerHTML={{ __html: message.html }} />
      )}

      {!message.html && (
        <Styles.ResponseText $src={message.src} $isFullScreen={fullScreen}>
          {message.text}
        </Styles.ResponseText>
      )}

      {mountRatings({ messageId: message.id, message })}
    </Styles.HistoryRow>
  );

  const executeAction = (action, params) => {
    const actions = {
      closeChat: () => handleCloseClick({ withTimout: false }),
      openSupport: () => handleOpenSupport({ params }),
      migration: () => handleRedirectToMigration(params),
      redirect: () => handleRedirect({ params }),
      openAlerts: () => handleOpenAlerts && handleOpenAlerts(),
    };

    return actions[action]();
  };

  const handleClickOptions = (option) => {
    handleSubmitMerlin && handleSubmitMerlin(option);
  };

  const mountOption = ({ message, index }) => (
    <Styles.HistoryRow>
      {message.src === SRC.BOT && (
        maiaIconRow()
      )}

      <Styles.HistoryColumn>
        {message.html && (
          <Styles.ResponseText dangerouslySetInnerHTML={{ __html: message.html }} />
        )}

        {!message.html && (
          <Styles.ResponseText>
            {message.text}
          </Styles.ResponseText>
        )}

        <Styles.OptionsList>
          {message.options && message.options.map(option => (
            <Styles.OptionItem key={`option-${option}`}>
              <Styles.ButtonOption
                type="button"
                onClick={() => handleClickOptions(typeof option === 'string' ? option : option.value)}
                disabled={index < (history.length - 1)}
              >
                {typeof option === 'string' ? option : option.label}
              </Styles.ButtonOption>
            </Styles.OptionItem>
          ))}
        </Styles.OptionsList>
      </Styles.HistoryColumn>

      {mountRatings({ messageId: message.id, message })}
    </Styles.HistoryRow>
  );

  const mountSteps = ({
    message, index,
  }) => (
    <>
      {(message.text || message.html) && mountText({ message })}
      <Styles.HistoryRow>
        <StepsAccordion
          steps={message.options}
          isFinished={index < history.length - 1}
          timeInSeconds={3}
        />
      </Styles.HistoryRow>
    </>
  );

  const setContent = (content) => {
    setModalContent(content);
  };

  const mountImage = ({ message }) => (
    <Styles.HistoryRow>
      {message.src === SRC.BOT && (
        maiaIconRow()
      )}

      {message.images && (
      <Styles.ResponseImage>
        {message.images.map(img => (
          <Styles.ImageWrapper onClick={() => setContent(<Styles.Image $full src={img.url} alt={img.alt} />)}>

            <Styles.Image src={img.url} alt={img.alt} $full={fullScreen} />
            <Styles.Overlay>
              <Styles.OverlayText>
                <IconZoom color="highPure" />
                {viewFullImage}
              </Styles.OverlayText>
            </Styles.Overlay>
          </Styles.ImageWrapper>
        ))}
      </Styles.ResponseImage>
      )}

      {!message.html && (
      <Styles.ResponseText $src={message.src}>
        {message.text}
      </Styles.ResponseText>
      )}
    </Styles.HistoryRow>
  );

  const mountfile = ({ message }) => (
    <Styles.HistoryRow>

      <Styles.DocumentMsgWrapper>
        <Styles.DocumentTitle>
          <IconDocumentFile size="md" />
          <Styles.DocumentTitleText>{locales.marketingPlanTitle}</Styles.DocumentTitleText>
        </Styles.DocumentTitle>

        <Styles.DocumentActions>
          <IconButton
            icon={<IconOpenEye size="md" color="primaryMedium" />}
            onClick={() => getPdf({ pdf: message.filename, type: '', action: openDocument })}
            testId="open-document-button"
          />

          <Styles.DocumentDownload
            onClick={() => getPdf({ pdf: message.filename, type: 'download' })}
            download="plano-de-marketing.pdf"
            target="_blank"
            data-testid="download-document-button"
          >
            <IconDownload size="md" color="primaryMedium" />
          </Styles.DocumentDownload>

        </Styles.DocumentActions>
      </Styles.DocumentMsgWrapper>
    </Styles.HistoryRow>
  );

  const mountSitePreview = ({ message }) => (
    <Styles.HistoryRow $hideOnDesktop>
      {message.src === SRC.BOT && (
        maiaIconRow()
      )}

      <Styles.DocumentMsgWrapper>
        <Styles.DocumentTitle>
          <IconDocumentFile size="md" />
          <Styles.DocumentTitleText>{locales.sitePreview}</Styles.DocumentTitleText>
        </Styles.DocumentTitle>

        <Styles.DocumentActions>
          <Styles.DocumentDownload
            onClick={() => openSitePreview({ url: message.params.url })}
            target="_blank"
          >
            <IconOpenEye size="md" color="primaryMedium" />
          </Styles.DocumentDownload>

        </Styles.DocumentActions>
      </Styles.DocumentMsgWrapper>
    </Styles.HistoryRow>
  );

  const onDownloadPdf = useCallback((url) => {
    const link = document.createElement('a');
    link.href = url.presigned_url;
    const fileName = url.file_name;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  useEffect(() => {
    if (pdfUrl) {
      onDownloadPdf(pdfUrl);
    }
  }, [pdfUrl, onDownloadPdf]);

  const unmountPDfLink = useCallback(() => {
    dispatch(commonActions.pdf.set(undefined));
  }, [dispatch]);

  useEffect(() => () => {
    unmountPDfLink();
  }, [unmountPDfLink]);

  const buildUrlWithParams = useCallback((baseUrl, params) => {
    if (!params || Object.keys(params).length === 0) {
      return baseUrl;
    }

    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      urlParams.append(key, value);
    });

    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${urlParams.toString()}`;
  }, []);

  const handleUrlAction = useCallback((action) => {
    if (action.internal) {
      return handleRedirect({ params: { internal: action.internal } });
    }

    const { url, params, target } = action;
    const finalUrl = buildUrlWithParams(url, params);

    if (target === 'blank') {
      return window.open(finalUrl, '_blank');
    }
    window.location.href = finalUrl;
  }, [buildUrlWithParams, handleRedirect]);

  const mountButton = ({ message, index }) => {
    const handleClick = () => {
      if (message.action) {
        if (message.action.type === 'url') {
          return handleUrlAction(message.action);
        }

        const openTicketRoute = `${routesLocale.help}${routesLocale.supportforms}`;
        const viewTicketsRoute = `${routesLocale.help}`;

        const actions = {
          openTicket: () => {
            if (isFullScreen) {
              onToggleFullScreen();
            }
            navHistory.push(openTicketRoute);
          },
          viewTickets: () => {
            if (isFullScreen) {
              onToggleFullScreen();
            }
            navHistory.push(viewTicketsRoute);
          },
          RedirectBrizy: () => dispatch(merlinActions.redirectToBrizy({
            withHgToken: message.action.params.withHgToken,
            target: message.action.params.target,
            builderId: message.action.params.builderId,
            domain: message.action.params.domain,
            index,
          })),
        };

        return actions[message.action.value]();
      }
    };

    const buttonIcon = {
      IconArrow: <IconArrow direction={message && message.icon && message.icon.direction} />,
    };

    return (
      <Styles.HistoryRow>
        <Button
          variant={message.style || 'primary'}
          iconRight={message && message.icon && message.icon && buttonIcon[message.icon.type]}
          label={message.label}
          onClick={handleClick}
          size="medium2"
          loading={executeN8nLoading && executeN8nLoading.bool && executeN8nLoading.index === index}
          disabled={executeN8nLoading && executeN8nLoading.bool && executeN8nLoading.index === index}
        />
      </Styles.HistoryRow>
    );
  };

  const mountMessage = ({ message, index }) => {
    if (message.action && message.action === 'preview') {
      return mountSitePreview({ message });
    }

    const rules = {
      text: mountText({ message }),
      action: () => executeAction(message.action, message.params),
      options: mountOption({ message, index }),
      steps: mountSteps({ message, index }),
      image: mountImage({ message }),
      file: mountfile({ message }),
      button: mountButton({ message, index }),
    };

    if (typeof rules[message.type] === 'function') {
      rules[message.type]();
    }

    return rules[message.type] || null;
  };

  if (!renderInHouseChat) {
    return;
  }

  return (
    <Styles.HistoryList
      ref={historyListRef}
      $hasScroll={
        historyListRef && historyListRef.current && historyListRef.current.scrollHeight > historyListRef.current.clientHeight
      }
    >
      {modalContent && (
        <Modal onClose={() => closeModal()} variant="highLight" maxWidth="990" maxHeight="600">
          {modalContent}
        </Modal>
      )}

      <Styles.HistoryListContent>
        {history.map((message, index) => (
          <Styles.ListItem key={JSON.stringify(message)}>
            {mountMessage({ message, index })}
          </Styles.ListItem>
        ))}

        {(loading) && (
        <Styles.ListItem>
          <Styles.HistoryRow>
            <Styles.MaiaIcon src={IconImage} $marginTop />
            <Styles.LoadingMessage>
              {loadingMessage}
            </Styles.LoadingMessage>
          </Styles.HistoryRow>
        </Styles.ListItem>
        )}
      </Styles.HistoryListContent>
    </Styles.HistoryList>
  );
};

export default ChatHistory;
